import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...rest }, ref) => {
    return (
      <input
        ref={ref}
        className={cn(
          'block w-full rounded-md bg-white px-3 h-10 text-sm text-breaker-ink',
          'border transition-colors placeholder:text-breaker-mute',
          invalid
            ? 'border-breaker-red focus-visible:ring-breaker-identity'
            : 'border-breaker-line-2 focus-visible:ring-breaker-identity',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-breaker-ground',
          'disabled:cursor-not-allowed disabled:bg-breaker-ground-2 disabled:text-breaker-mute',
          className,
        )}
        {...rest}
      />
    );
  },
);
Input.displayName = 'Input';

interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {}

export function Label({ className, ...rest }: LabelProps) {
  return (
    <label
      className={cn('block text-xs font-medium text-breaker-ink-2 mb-1.5', className)}
      {...rest}
    />
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: React.ReactNode;
}

export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-breaker-red mt-1.5">{error}</p>
      ) : hint ? (
        <p className="text-xs text-breaker-mute mt-1.5">{hint}</p>
      ) : null}
    </div>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, ...rest }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(
          'block w-full rounded-md bg-white px-3 py-2 text-sm text-breaker-ink leading-5',
          'border transition-colors placeholder:text-breaker-mute resize-y',
          invalid
            ? 'border-breaker-red focus-visible:ring-breaker-identity'
            : 'border-breaker-line-2 focus-visible:ring-breaker-identity',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-breaker-ground',
          'disabled:cursor-not-allowed disabled:bg-breaker-ground-2 disabled:text-breaker-mute',
          className,
        )}
        {...rest}
      />
    );
  },
);
Textarea.displayName = 'Textarea';
